/**
 * ACADS — the month's reconciliation (HQ view), laid out like the finance
 * reco sheet.
 *
 * It opens on a summary: one line per way money came in during the month
 * (cash, bank transfer, UPI, paid to teacher, ...). A line opens its detail:
 *
 *   Cash             the centre's cash box, date by date: opening balance,
 *                    cash received (from the fee receipts), what went out,
 *                    and what carries to next month
 *   Bank, UPI, ...   one line per receipt, marked against the bank statement:
 *                    Matched, Doubtful or To check
 *   Paid to teacher  the receipts, the OH share owed on them, and what each
 *                    teacher has handed over
 *
 * Receipts are never typed here. Only what the receipts cannot know is added:
 * a deposit, a dispatch, stationery, OH share received, the opening balance.
 * Month and centre come from the page's top bar.
 *
 * The centre page shows a plain cash register instead (`cashOnly`): one row
 * per cash receipt or cash-book entry, in the order they happened, with the
 * cash in hand after each. The team adds what went out and what came in;
 * setting the opening balance and removing a line stay with HQ.
 */
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import {
  addAcadsCashEntry,
  fetchAcadsCashbook,
  fetchAcadsTeachers,
  removeAcadsCashEntry,
  setAcadsBankCheck
} from 'api'
import constants from 'app/constants'
import { showToast } from 'app/helpers'

const CENTRES = [
  ['TR', 'Theatre Road'],
  ['SL', 'Salt Lake'],
  ['', 'Both centres']
]
const CHECKS = {
  matched: 'Matched',
  doubtful: 'Doubtful',
  '': 'To check'
}
const CASH = 'Cash'
const PAID_TO_TEACHER = 'Paid to teacher'

const useStyles = makeStyles((theme) => ({
  key: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: '#666',
    fontWeight: 600
  },
  sub: { display: 'block', fontSize: 12, color: theme.palette.text.secondary },
  bad: { color: '#a23c19' },
  good: { color: '#1c6b43' },
  head: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    margin: theme.spacing(3, 0, 1)
  },
  table: {
    borderCollapse: 'collapse',
    width: '100%',
    border: '1px solid #e4e7ec',
    '& th, & td': {
      borderBottom: '1px solid #ececec',
      padding: '8px 12px',
      textAlign: 'left',
      verticalAlign: 'middle'
    },
    '& th': {
      background: '#f4f6f8',
      fontSize: 11,
      textTransform: 'uppercase',
      letterSpacing: '0.03em',
      color: '#555'
    }
  },
  num: { textAlign: 'right !important', fontVariantNumeric: 'tabular-nums' },
  out: { color: '#a23c19' },
  total: { '& td': { background: '#f4f6f8', fontWeight: 700 } },
  click: { cursor: 'pointer', '&:hover td': { background: '#f0f6ff' } },
  // Inside the pop-up the list scrolls, and its heading stays at the top of
  // the pop-up.
  sticky: {
    borderCollapse: 'separate',
    borderSpacing: 0,
    '& th': { position: 'sticky', top: 0, zIndex: 2 }
  },
  find: {
    display: 'block',
    minWidth: 70,
    '& input': {
      padding: '5px 7px',
      fontSize: 12,
      background: '#fff',
      '&::placeholder': { color: '#444', opacity: 1, fontWeight: 600 }
    }
  },
  open: { color: theme.palette.primary.main, whiteSpace: 'nowrap' },
  matched: { background: '#e3f3ea', color: '#1c6b43', fontWeight: 600 },
  doubtful: { background: '#fde9e1', color: '#a23c19', fontWeight: 600 },
  todo: { background: '#eceef1', color: '#555', fontWeight: 600 },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minWidth: 300,
    paddingTop: theme.spacing(1)
  },
  chips: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(0.75) }
}))

const inr = (v) =>
  `${Number(v) < 0 ? '−' : ''}₹${Math.abs(Number(v || 0)).toLocaleString(
    'en-IN',
    { maximumFractionDigits: 0 }
  )}`
const day = (value) => dayjs(value).format('D MMM')
const errorText = (err, fallback) =>
  (err.response && err.response.data && err.response.data.detail) || fallback

// A cash-book line: money out of the box, or OH share handed over by a teacher.
const EntryDialog = ({ data, centre, teachers, onClose, onSaved, classes }) => {
  const [form, setForm] = useState({
    kind: 'deposit',
    centre: centre || 'TR',
    date:
      dayjs().format('YYYY-MM') === data.month
        ? dayjs().format('YYYY-MM-DD')
        : `${data.month}-01`,
    amount: '',
    teacher: '',
    mode: CASH,
    note: ''
  })
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState(false)
  const fileInput = useRef(null)
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value })

  const isShare = form.kind === 'oh_share'
  const needsTeacher = isShare || form.kind === 'dispatch'
  // A stationery spend needs the bill; a non-cash OH share its screenshot.
  const isStationery = form.kind === 'stationery'
  const isDispatch = form.kind === 'dispatch'
  const needsFile =
    (isShare && form.mode !== CASH) || isStationery || isDispatch
  let label = 'Save'
  if (!(Number(form.amount) > 0)) label = 'Type the amount'
  else if (needsTeacher && !form.teacher) label = 'Pick the teacher'
  else if (needsFile && !file)
    label = isStationery
      ? 'Attach the bill'
      : isDispatch
      ? 'Attach the photo'
      : 'Attach the screenshot'
  const ready = label === 'Save' && !busy

  const save = async () => {
    setBusy(true)
    try {
      await addAcadsCashEntry(form, file)
      showToast('Saved.', constants.SUCCESS)
      onSaved()
    } catch (err) {
      showToast(errorText(err, 'Could not save the entry'), constants.ERROR)
      setBusy(false)
    }
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose}>
      <DialogTitle>Add to the cash book</DialogTitle>
      <DialogContent>
        <div className={classes.form}>
          <TextField
            select
            label="What is it"
            size="small"
            variant="outlined"
            value={form.kind}
            onChange={set('kind')}
          >
            {data.kinds.map((k) => (
              <MenuItem key={k.kind} value={k.kind}>
                {k.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Centre"
            size="small"
            variant="outlined"
            value={form.centre}
            onChange={set('centre')}
          >
            <MenuItem value="TR">Theatre Road</MenuItem>
            <MenuItem value="SL">Salt Lake</MenuItem>
          </TextField>
          <TextField
            label="Date"
            type="date"
            size="small"
            variant="outlined"
            value={form.date}
            onChange={set('date')}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Amount"
            type="number"
            size="small"
            variant="outlined"
            value={form.amount}
            onChange={set('amount')}
          />
          {needsTeacher && (
            <TextField
              select
              label="Teacher"
              size="small"
              variant="outlined"
              value={form.teacher}
              onChange={set('teacher')}
            >
              {teachers.map((t) => (
                <MenuItem key={t.uuid} value={t.uuid}>
                  {t.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          {isShare && (
            <div>
              <div className={classes.key}>How the teacher paid</div>
              <div className={classes.chips} style={{ marginTop: 6 }}>
                {data.modes
                  .filter((m) => m !== PAID_TO_TEACHER)
                  .map((m) => (
                    <Chip
                      key={m}
                      label={m}
                      clickable
                      color={form.mode === m ? 'primary' : 'default'}
                      onClick={() => setForm({ ...form, mode: m })}
                    />
                  ))}
              </div>
            </div>
          )}
          <TextField
            label="Remark (slip number, what it was for)"
            size="small"
            variant="outlined"
            value={form.note}
            onChange={set('note')}
          />
          <div className={classes.chips}>
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                setFile(e.target.files[0] || null)
                e.target.value = ''
              }}
            />
            <Button
              size="small"
              variant={needsFile && !file ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => fileInput.current.click()}
            >
              {file ? 'Change photo' : 'Attach photo'}
            </Button>
            {file ? (
              <Chip label={file.name} onDelete={() => setFile(null)} />
            ) : (
              <Typography variant="caption" color="textSecondary">
                {isStationery
                  ? 'Needed: a photo of the stationery bill.'
                  : isDispatch
                  ? 'Needed: a photo of the dispatch given to the teacher.'
                  : needsFile
                  ? `Needed for ${form.mode}.`
                  : 'Optional: deposit slip or bill.'}
              </Typography>
            )}
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          Close
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={!ready}
          onClick={save}
        >
          {label}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const OpeningDialog = ({ data, centre, onClose, onSaved, classes }) => {
  const [at, setAt] = useState(centre || 'TR')
  const [amount, setAmount] = useState('')
  const [busy, setBusy] = useState(false)
  const save = async () => {
    setBusy(true)
    try {
      await addAcadsCashEntry({
        kind: 'opening',
        centre: at,
        month: data.month,
        amount
      })
      showToast('Opening balance set.', constants.SUCCESS)
      onSaved()
    } catch (err) {
      showToast(errorText(err, 'Could not set the opening'), constants.ERROR)
      setBusy(false)
    }
  }
  return (
    <Dialog open onClose={busy ? undefined : onClose}>
      <DialogTitle>Opening cash for {data.month_label}</DialogTitle>
      <DialogContent>
        <div className={classes.form}>
          <Typography variant="body2">
            Set this once, for the first month in Alfred. After that each month
            opens with what the month before closed on.
          </Typography>
          <TextField
            select
            label="Centre"
            size="small"
            variant="outlined"
            value={at}
            onChange={(e) => setAt(e.target.value)}
          >
            <MenuItem value="TR">Theatre Road</MenuItem>
            <MenuItem value="SL">Salt Lake</MenuItem>
          </TextField>
          <TextField
            autoFocus
            label="Cash in hand at the start of the month"
            type="number"
            size="small"
            variant="outlined"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          Close
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={busy || amount === '' || Number(amount) < 0}
          onClick={save}
        >
          Set opening
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// HQ's mark on one non-cash line.
const CheckDialog = ({ row, onClose, onSaved, classes }) => {
  const [status, setStatus] = useState(row.check || 'matched')
  const [note, setNote] = useState(row.check_note || '')
  const [busy, setBusy] = useState(false)
  const needsNote = status === 'doubtful' && !note.trim()
  const save = async () => {
    setBusy(true)
    try {
      await setAcadsBankCheck({
        source: row.source,
        uuid: row.uuid,
        status,
        note
      })
      onSaved()
    } catch (err) {
      showToast(errorText(err, 'Could not save the check'), constants.ERROR)
      setBusy(false)
    }
  }
  return (
    <Dialog open onClose={busy ? undefined : onClose}>
      <DialogTitle>
        {row.who} · {inr(row.amount)} · {row.mode}
      </DialogTitle>
      <DialogContent>
        <div className={classes.form}>
          <Typography variant="body2">
            {day(row.date)}
            {row.receipt_no ? ` · ${row.receipt_no}` : ''}. Is this amount in
            the bank statement?
          </Typography>
          <div className={classes.chips}>
            {Object.keys(CHECKS).map((key) => (
              <Chip
                key={key || 'todo'}
                label={CHECKS[key]}
                clickable
                color={status === key ? 'primary' : 'default'}
                onClick={() => setStatus(key)}
              />
            ))}
          </div>
          <TextField
            multiline
            rows={2}
            label={
              status === 'doubtful'
                ? 'Why is it doubtful? (required)'
                : 'Remark (optional)'
            }
            variant="outlined"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          Close
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={busy || needsNote}
          onClick={save}
        >
          {needsNote ? 'Write the reason' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const AcadsReco = ({ month, centre, cashOnly = false }) => {
  const classes = useStyles()
  const [data, setData] = useState(null)
  const [failed, setFailed] = useState(false)
  const [teachers, setTeachers] = useState([])
  const [adding, setAdding] = useState(false)
  const [opening, setOpening] = useState(false)
  const [checking, setChecking] = useState(null)
  // The mode whose detail is open in the pop-up, and what is typed into its
  // heading's search boxes.
  const [openMode, setOpenMode] = useState('')
  const [find, setFind] = useState({})
  const openDetail = (mode) => {
    setFind({})
    setOpenMode(mode)
  }

  const load = useCallback(
    () =>
      fetchAcadsCashbook(month, centre)
        .then((res) => {
          setData(res)
          setFailed(false)
        })
        .catch(() => setFailed(true)),
    [month, centre]
  )
  useEffect(() => {
    setData(null)
    load()
  }, [load])
  useEffect(() => {
    fetchAcadsTeachers()
      .then(setTeachers)
      .catch(() => setTeachers([]))
  }, [])

  const done = () => {
    setAdding(false)
    setOpening(false)
    setChecking(null)
    load()
  }
  const remove = async (line) => {
    try {
      await removeAcadsCashEntry(line.uuid)
      showToast('Removed.', constants.SUCCESS)
      load()
    } catch (err) {
      showToast(errorText(err, 'Could not remove it'), constants.ERROR)
    }
  }

  if (!data) {
    return failed ? (
      <Typography color="error">
        Could not load the reconciliation. Refresh the page.
      </Typography>
    ) : (
      <CircularProgress size={24} />
    )
  }

  const { cash, bank, fees } = data
  const where = (CENTRES.find(([value]) => value === data.centre) ||
    CENTRES[2])[1]
  const sum = (rows) => rows.reduce((total, r) => total + r.amount, 0)
  const stillToCome = data.teachers.reduce((total, t) => total + t.balance, 0)

  const cashLines = [
    ...cash.days.map((d) => ({
      key: `in-${d.date}`,
      date: d.date,
      what: 'Cash received',
      inAmount: d.amount,
      note: `${d.receipts} receipt${d.receipts === 1 ? '' : 's'}`
    })),
    ...cash.lines.map((l) => ({
      key: l.uuid,
      date: l.date,
      what: l.label,
      inAmount: l.out ? 0 : l.amount,
      outAmount: l.out ? l.amount : 0,
      note: [l.teacher, l.note].filter(Boolean).join(' · '),
      proof: l.proof,
      entry: l
    }))
  ].sort((a, b) => {
    if (a.date === b.date) return 0
    return a.date < b.date ? -1 : 1
  })

  // The two-step cash check. Step 1, day by day: the cash a centre took in,
  // less what it spent (stationery, dispatch to a teacher), should go to
  // Rajkumar ji. Step 2: what Rajkumar ji got should reach the bank.
  const byDay = {}
  const dayRow = (date) => {
    if (!byDay[date]) {
      byDay[date] = { date, received: 0, spent: 0, submitted: 0 }
    }
    return byDay[date]
  }
  cash.days.forEach((d) => {
    dayRow(d.date).received += d.amount
  })
  cash.lines.forEach((l) => {
    if (l.kind === 'oh_share') dayRow(l.date).received += l.amount
    else if (l.kind === 'stationery' || l.kind === 'dispatch') {
      dayRow(l.date).spent += l.amount
    } else if (l.kind === 'handover') dayRow(l.date).submitted += l.amount
  })
  let atCentre = cash.opening
  const steps = Object.values(byDay)
    .sort((a, b) => (a.date < b.date ? -1 : 1))
    .map((r) => {
      atCentre += r.received - r.spent - r.submitted
      return { ...r, toSubmit: r.received - r.spent, atCentre }
    })
  const kindTotal = (kind) =>
    cash.lines.filter((l) => l.kind === kind).reduce((t, l) => t + l.amount, 0)
  const submitted = kindTotal('handover')
  const deposited = kindTotal('deposit')
  const withRajkumar = submitted - deposited

  // One line per way money came in. Cash is always shown; the rest only when
  // something was received that way.
  const summary = data.modes
    .map((mode) => {
      if (mode === CASH) {
        return {
          mode,
          receipts: fees.receipts[mode] || 0,
          amount: fees.by_mode[mode] || 0,
          note: `Not yet submitted to Rajkumar ji ${inr(
            atCentre
          )} · with Rajkumar ji, not in bank ${inr(withRajkumar)}`,
          bad: Math.round(atCentre) !== 0 || Math.round(withRajkumar) !== 0
        }
      }
      if (mode === PAID_TO_TEACHER) {
        return {
          mode,
          receipts: data.paid_to_teacher.length,
          amount: sum(data.paid_to_teacher),
          note: `OH share still to come ${inr(stillToCome)}`,
          bad: stillToCome > 0
        }
      }
      const rows = bank.rows.filter((r) => r.mode === mode)
      const open = rows.filter((r) => r.check !== 'matched')
      return {
        mode,
        receipts: rows.length,
        amount: sum(rows),
        note: open.length
          ? `${open.length} not matched with the bank yet (${inr(sum(open))})`
          : 'All matched with the bank',
        bad: open.length > 0
      }
    })
    .filter((line) => line.mode === CASH || line.receipts > 0)

  const proofLink = (url, label) =>
    url ? (
      <a href={url} target="_blank" rel="noopener noreferrer">
        {label}
      </a>
    ) : (
      '–'
    )

  // A heading cell whose name is its own search box, as in Daily receipts.
  const searchHead = (key, label, extra) => (
    <th className={extra}>
      <TextField
        className={classes.find}
        variant="outlined"
        size="small"
        placeholder={label}
        title={`Type to search ${label.toLowerCase()}`}
        value={find[key] || ''}
        onChange={(e) => setFind({ ...find, [key]: e.target.value })}
      />
    </th>
  )
  const found = (rows, text) =>
    rows.filter((r) => {
      const words = text(r)
      return Object.keys(find).every((key) => {
        const term = (find[key] || '').trim().toLowerCase()
        return (
          !term ||
          String(words[key] || '')
            .toLowerCase()
            .includes(term)
        )
      })
    })
  const who = (r) => (
    <td>
      <b>{r.who}</b>
      <span className={classes.sub}>
        {[r.phone, r.receipt_no, r.about].filter(Boolean).join(' · ')}
      </span>
    </td>
  )

  const twoSteps = (
    <>
      <Typography variant="subtitle2" style={{ marginTop: 4 }}>
        Step 1 · Cash received vs submitted to Rajkumar ji, day by day
      </Typography>
      <table className={classes.table} style={{ marginBottom: 16 }}>
        <thead>
          <tr>
            <th>Date</th>
            <th className={classes.num}>Cash received</th>
            <th className={classes.num}>Spent (stationery, dispatch)</th>
            <th className={classes.num}>To submit</th>
            <th className={classes.num}>Submitted to Rajkumar ji</th>
            <th className={classes.num}>Still at the centre</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Start of month</td>
            <td colSpan={4} />
            <td className={classes.num}>{inr(cash.opening)}</td>
          </tr>
          {steps.map((r) => (
            <tr key={r.date}>
              <td>{day(r.date)}</td>
              <td className={classes.num}>{inr(r.received)}</td>
              <td className={`${classes.num} ${classes.out}`}>
                {r.spent ? inr(r.spent) : ''}
              </td>
              <td className={classes.num}>{inr(r.toSubmit)}</td>
              <td className={classes.num}>
                {r.submitted ? inr(r.submitted) : ''}
              </td>
              <td
                className={classes.num}
                style={
                  Math.round(r.atCentre) ? { color: '#a23c19' } : undefined
                }
              >
                {inr(r.atCentre)}
              </td>
            </tr>
          ))}
          <tr className={classes.total}>
            <td>Month</td>
            <td className={classes.num}>
              {inr(steps.reduce((t, r) => t + r.received, 0))}
            </td>
            <td className={`${classes.num} ${classes.out}`}>
              {inr(steps.reduce((t, r) => t + r.spent, 0))}
            </td>
            <td className={classes.num}>
              {inr(steps.reduce((t, r) => t + r.toSubmit, 0))}
            </td>
            <td className={classes.num}>{inr(submitted)}</td>
            <td className={classes.num}>{inr(atCentre)}</td>
          </tr>
        </tbody>
      </table>

      <Typography variant="subtitle2">
        Step 2 · Submitted to Rajkumar ji vs deposited in bank
      </Typography>
      <table className={classes.table} style={{ marginBottom: 16 }}>
        <tbody>
          <tr>
            <td>Submitted to Rajkumar ji this month</td>
            <td className={classes.num}>{inr(submitted)}</td>
          </tr>
          <tr>
            <td>Deposited in bank this month</td>
            <td className={classes.num}>{inr(deposited)}</td>
          </tr>
          <tr className={classes.total}>
            <td>With Rajkumar ji, not yet in the bank</td>
            <td
              className={classes.num}
              style={
                Math.round(withRajkumar) ? { color: '#a23c19' } : undefined
              }
            >
              {inr(withRajkumar)}
            </td>
          </tr>
        </tbody>
      </table>
      <Typography variant="subtitle2">Cash book</Typography>
    </>
  )

  const cashDetail = (
    <>
      <div className={classes.head} style={{ marginTop: 0 }}>
        <Typography variant="body2" color="textSecondary">
          Cash received comes from the receipts. Add what went out (stationery,
          dispatch, submitted to Rajkumar ji, bank deposit), and OH share a
          teacher handed over in cash.
        </Typography>
        {!data.frozen && (
          <>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => setAdding(true)}
            >
              + Add entry
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setOpening(true)}
            >
              Set opening balance
            </Button>
          </>
        )}
      </div>
      {twoSteps}
      <table className={`${classes.table} ${classes.sticky}`}>
        <thead>
          <tr>
            <th>Date</th>
            <th>What</th>
            <th className={classes.num}>In</th>
            <th className={classes.num}>Out</th>
            <th>Remark</th>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Start of month</td>
            <td>Opening balance</td>
            <td className={classes.num}>{inr(cash.opening)}</td>
            <td />
            <td>
              {cash.opening_set_here
                ? 'Set by hand for this month'
                : 'Carried from the month before'}
            </td>
            <td />
          </tr>
          {cashLines.map((l) => (
            <tr key={l.key}>
              <td>{day(l.date)}</td>
              <td>{l.what}</td>
              <td className={classes.num}>
                {l.inAmount ? inr(l.inAmount) : ''}
              </td>
              <td className={`${classes.num} ${classes.out}`}>
                {l.outAmount ? inr(l.outAmount) : ''}
              </td>
              <td>
                {l.note} {l.proof ? proofLink(l.proof, 'photo') : ''}
              </td>
              <td>
                {l.entry && !data.frozen && (
                  <Button size="small" onClick={() => remove(l.entry)}>
                    Remove
                  </Button>
                )}
              </td>
            </tr>
          ))}
          <tr className={classes.total}>
            <td colSpan={2}>Cash in hand, carried to next month</td>
            <td className={classes.num}>{inr(cash.opening + cash.received)}</td>
            <td className={`${classes.num} ${classes.out}`}>{inr(cash.out)}</td>
            <td colSpan={2}>{inr(cash.closing)}</td>
          </tr>
        </tbody>
      </table>
    </>
  )

  const bankDetail = (mode) => {
    const all = bank.rows.filter((r) => r.mode === mode)
    const rows = found(all, (r) => ({
      date: day(r.date),
      who: [r.who, r.phone, r.receipt_no, r.about].join(' '),
      amount: Math.round(r.amount),
      check: CHECKS[r.check] || CHECKS[''],
      note: r.check_note
    }))
    const matched = sum(rows.filter((r) => r.check === 'matched'))
    return (
      <>
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ margin: '8px 0' }}
        >
          One line per receipt. Click the mark to check it against the bank
          statement. Type in a heading to search.
        </Typography>
        <table className={`${classes.table} ${classes.sticky}`}>
          <thead>
            <tr>
              <th>S.No</th>
              {searchHead('date', 'Date')}
              {searchHead('who', 'Student, phone, receipt no.')}
              {searchHead('amount', 'Amount', classes.num)}
              <th>Receipt</th>
              {searchHead('check', 'Bank check')}
              {searchHead('note', 'Remark')}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.uuid}>
                <td>{i + 1}</td>
                <td>{day(r.date)}</td>
                {who(r)}
                <td className={classes.num}>{inr(r.amount)}</td>
                <td>{proofLink(r.proof, 'View')}</td>
                <td>
                  <Chip
                    size="small"
                    clickable
                    className={classes[r.check || 'todo']}
                    label={CHECKS[r.check] || CHECKS['']}
                    onClick={() => setChecking(r)}
                  />
                </td>
                <td>{r.check_note}</td>
              </tr>
            ))}
            <tr className={classes.total}>
              <td colSpan={3}>
                Total · {rows.length}
                {rows.length !== all.length ? ` of ${all.length}` : ''} receipts
                · matched {inr(matched)}
              </td>
              <td className={classes.num}>{inr(sum(rows))}</td>
              <td colSpan={3} />
            </tr>
          </tbody>
        </table>
      </>
    )
  }

  const paidRows = found(data.paid_to_teacher, (r) => ({
    date: day(r.date),
    who: [r.who, r.phone, r.receipt_no].join(' '),
    teacher: r.teacher,
    amount: Math.round(r.amount)
  }))
  const teacherDetail = (
    <>
      <table className={`${classes.table} ${classes.sticky}`}>
        <thead>
          <tr>
            <th>S.No</th>
            {searchHead('date', 'Date')}
            {searchHead('who', 'Student, phone, receipt no.')}
            {searchHead('teacher', 'Teacher')}
            {searchHead('amount', 'Amount', classes.num)}
            <th>Receipt</th>
          </tr>
        </thead>
        <tbody>
          {paidRows.map((r, i) => (
            <tr key={r.uuid}>
              <td>{i + 1}</td>
              <td>{day(r.date)}</td>
              {who({ ...r, about: '' })}
              <td>{r.teacher}</td>
              <td className={classes.num}>{inr(r.amount)}</td>
              <td>{proofLink(r.proof, 'View')}</td>
            </tr>
          ))}
          <tr className={classes.total}>
            <td colSpan={4}>Total · {paidRows.length} receipts</td>
            <td className={classes.num}>{inr(sum(paidRows))}</td>
            <td />
          </tr>
        </tbody>
      </table>
      <div className={classes.head}>
        <Typography variant="subtitle2">
          <b>OH share on what teachers were paid directly</b>
        </Typography>
        <Typography variant="body2" color="textSecondary">
          What a teacher hands over is added from Cash, as OH share received.
        </Typography>
      </div>
      <table className={classes.table}>
        <thead>
          <tr>
            <th>Teacher</th>
            <th className={classes.num}>Paid to teacher this month</th>
            <th className={classes.num}>To date</th>
            <th className={classes.num}>OH share due</th>
            <th className={classes.num}>OH share received</th>
            <th className={classes.num}>Still to come</th>
          </tr>
        </thead>
        <tbody>
          {data.teachers.map((t) => (
            <tr key={t.teacher_uuid}>
              <td>{t.teacher}</td>
              <td className={classes.num}>{inr(t.paid_this_month)}</td>
              <td className={classes.num}>{inr(t.paid_to_date)}</td>
              <td className={classes.num}>{inr(t.oh_share_due)}</td>
              <td className={classes.num}>{inr(t.oh_share_received)}</td>
              <td
                className={`${classes.num} ${
                  t.balance > 0 ? classes.bad : classes.good
                }`}
              >
                <b>{inr(t.balance)}</b>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )

  const dialogs = (
    <>
      {adding && (
        <EntryDialog
          data={data}
          centre={centre}
          teachers={teachers}
          onClose={() => setAdding(false)}
          onSaved={done}
          classes={classes}
        />
      )}
      {opening && (
        <OpeningDialog
          data={data}
          centre={centre}
          onClose={() => setOpening(false)}
          onSaved={done}
          classes={classes}
        />
      )}
      {checking && (
        <CheckDialog
          row={checking}
          onClose={() => setChecking(null)}
          onSaved={done}
          classes={classes}
        />
      )}
    </>
  )

  if (cashOnly) {
    const entries = [
      ...cash.receipts.map((r) => ({
        key: r.key,
        date: r.date,
        at: r.at || '',
        what: `Fee · ${r.student}`,
        sub: [
          r.receipt_no !== '–' && r.receipt_no,
          r.cash_receipt_no && `Cash receipt ${r.cash_receipt_no}`,
          r.teacher,
          r.months.join(', ')
        ],
        inAmount: r.amount,
        outAmount: 0
      })),
      ...cash.lines.map((l) => ({
        key: l.uuid,
        date: l.date,
        at: l.at || '',
        what: l.label,
        sub: [l.teacher, l.note],
        inAmount: l.out ? 0 : l.amount,
        outAmount: l.out ? l.amount : 0,
        proof: l.proof
      }))
    ].sort((a, b) => {
      if (a.date !== b.date) return a.date < b.date ? -1 : 1
      if (a.at === b.at) return 0
      return a.at < b.at ? -1 : 1
    })
    let inHand = cash.opening
    const rows = entries.map((e) => {
      inHand += e.inAmount - e.outAmount
      return { ...e, inHand }
    })
    return (
      <div>
        <div className={classes.head} style={{ marginTop: 0 }}>
          <Typography variant="subtitle1">
            <b>Cash register · {data.month_label}</b> · {where} · Cash in hand{' '}
            <b>{inr(cash.closing)}</b>
          </Typography>
          {data.frozen ? (
            <Chip size="small" label="Month is frozen" />
          ) : (
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => setAdding(true)}
            >
              + Add entry
            </Button>
          )}
        </div>
        <table className={classes.table}>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Date</th>
              <th>Particulars</th>
              <th className={classes.num}>Cash in</th>
              <th className={classes.num}>Cash out</th>
              <th className={classes.num}>Cash in hand</th>
              <th>Photo</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td />
              <td>{day(`${data.month}-01`)}</td>
              <td>
                <b>Opening balance</b>
              </td>
              <td />
              <td />
              <td className={classes.num}>{inr(cash.opening)}</td>
              <td />
            </tr>
            {rows.map((r, i) => (
              <tr key={r.key}>
                <td>{i + 1}</td>
                <td>{day(r.date)}</td>
                <td>
                  <b>{r.what}</b>
                  <span className={classes.sub}>
                    {r.sub.filter(Boolean).join(' · ')}
                  </span>
                </td>
                <td className={classes.num}>
                  {r.inAmount ? inr(r.inAmount) : ''}
                </td>
                <td className={`${classes.num} ${classes.out}`}>
                  {r.outAmount ? inr(r.outAmount) : ''}
                </td>
                <td className={classes.num}>{inr(r.inHand)}</td>
                <td>{r.proof ? proofLink(r.proof, 'View') : ''}</td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={7}>No cash received or spent this month yet.</td>
              </tr>
            )}
            <tr className={classes.total}>
              <td colSpan={3}>Total · cash in hand at the end</td>
              <td className={classes.num}>{inr(cash.received)}</td>
              <td className={`${classes.num} ${classes.out}`}>
                {inr(cash.out)}
              </td>
              <td className={classes.num}>{inr(cash.closing)}</td>
              <td />
            </tr>
          </tbody>
        </table>
        {dialogs}
      </div>
    )
  }

  let detail = null
  if (openMode === CASH) detail = cashDetail
  else if (openMode === PAID_TO_TEACHER) detail = teacherDetail
  else if (openMode) detail = bankDetail(openMode)

  return (
    <div>
      <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
        <b>Received in {data.month_label}</b> · {where}
        {data.frozen && (
          <Chip
            size="small"
            label="Month is frozen"
            style={{ marginLeft: 12 }}
          />
        )}
      </Typography>
      <table className={classes.table}>
        <thead>
          <tr>
            <th>How it came</th>
            <th className={classes.num}>Receipts</th>
            <th className={classes.num}>Amount</th>
            <th>Where it stands</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {summary.map((line) => (
            <tr
              key={line.mode}
              className={classes.click}
              onClick={() => openDetail(line.mode)}
            >
              <td>
                <b>{line.mode}</b>
              </td>
              <td className={classes.num}>{line.receipts}</td>
              <td className={classes.num}>{inr(line.amount)}</td>
              <td className={line.bad ? classes.bad : undefined}>
                {line.note}
              </td>
              <td className={classes.open}>See details ›</td>
            </tr>
          ))}
          <tr className={classes.total}>
            <td>Total received</td>
            <td className={classes.num}>
              {summary.reduce((total, line) => total + line.receipts, 0)}
            </td>
            <td className={classes.num}>{inr(sum(summary))}</td>
            <td
              className={bank.difference ? classes.bad : classes.good}
              colSpan={2}
            >
              Difference to be resolved with the bank: {inr(bank.difference)}
            </td>
          </tr>
        </tbody>
      </table>

      {openMode && (
        <Dialog open fullWidth maxWidth="md" onClose={() => setOpenMode('')}>
          <DialogTitle>
            {openMode} · {data.month_label} · {where}
          </DialogTitle>
          <DialogContent style={{ paddingTop: 0 }}>{detail}</DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenMode('')}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
      {dialogs}
    </div>
  )
}

export default AcadsReco
